import React, { useEffect, useState, useMemo } from "react";
import Select from "../../../shared/Select";
import {
  useAssessmentType,
  useEducationLevelByAssessmentModule,
  useEducationLevelDivisions,
} from "../../../../hooks/useAssessment";
import { useARRCode } from "../../../../context/ARRLevelContext";
import AssessmentConfigurationModal from "./AssessmentConfigurationModal";
import CustomModal from "../../../shared/CustomModal";
import swal from "sweetalert";

const recordingDateOption = [
  {
    label: "1",
    value: "1",
  },
  {
    label: "2",
    value: "2",
  },
  {
    label: "3",
    value: "3",
  },
  {
    label: "4",
    value: "4",
  },
  {
    label: "5",
    value: "5",
  },
  {
    label: "6",
    value: "6",
  },
];

const recordingOption = [
  {
    label: "Days",
    value: "days",
  },
  {
    label: "Weeks",
    value: "weeks",
  },
  {
    label: "Months",
    value: "months",
  },
];

const AssessmentDetailForm = ({
  isEdit,
  data,
  formData,
  setFormData,
  assessmentName,
  setAssessmentName,
  yearLevelId,
  yearLevel,
  assessments,
  aggregationWeightConfiguration,
  setAggregationWeightConfiguration,
  setAssessmentSubjectConfigurations,
  setAssessmentGeneralConfigurations,
  setYearLevelId,
  setRecordingDateCheckbox,
  setRecordingDate,
  getYearLevelByEducationLevel,
  recordingDateCheckbox,
  recordingDate,
}) => {
  const { arrLevel } = useARRCode();
  const { educationLevel } = useEducationLevelByAssessmentModule();
  const { educationDivision, getEducationLevelDivision } =
    useEducationLevelDivisions();
  const { assessmentType } = useAssessmentType();
  const [aggregationConfigurationModal, setAggregationConfigurationModal] =
    useState(false);

  const yearLevelOption = useMemo(() => {
    return (
      yearLevel?.map((item) => ({ label: item.name, value: item.id })) || []
    );
  }, [yearLevel]);

  const assessmentsOption = useMemo(() => {
    return assessments
      ?.filter((item) => item?.id !== data?.id)
      ?.map((item) => ({
        label: item.assessmentName,
        value: item.id,
        assessmentTypeCode: item.assessmentTypeCode,
      }));
  }, [assessments]);

  //set education level in select input
  useEffect(() => {
    if (educationLevel?.length > 0) {
      setFormData((prev) => ({
        ...prev,
        educationLevelId: educationLevel[0]?.value,
      }));
    } else {
      setFormData((prev) => ({ ...prev, educationLevelId: "" }));
    }
  }, [educationLevel, arrLevel]);

  //fetch term after the fetching of education level
  useEffect(() => {
    if (formData.educationLevelId) {
      getEducationLevelDivision(formData.educationLevelId);
    }
  }, [formData.educationLevelId]);

  //fecth classes after education level and term
  useEffect(() => {
    if (formData.educationLevelId && formData.educationLevelDivisionId) {
      getYearLevelByEducationLevel(
        formData.educationLevelId,
        formData.educationLevelDivisionId,
      );
    }
  }, [formData.educationLevelId, formData.educationLevelDivisionId]);

  return (
    <>
      <div className={`arr-form grid  ${isEdit ? "noShadow" : ""}`}>
        <div className="form-input">
          <label className="required" htmlFor="educationLevelId">
            Education Level
          </label>
          <Select
            value={
              educationLevel.find(
                (o) => o.value === formData?.educationLevelId,
              ) || null
            }
            options={educationLevel}
            isClearable={false}
            isDisabled={true}
          />
        </div>

        <div className="form-input">
          <label className="required" htmlFor="term">
            Term
          </label>
          <Select
            value={
              educationDivision.find(
                (o) => o.value === formData.educationLevelDivisionId,
              ) || null
            }
            options={educationDivision}
            onChange={(option) => {
              setFormData((prev) => ({
                ...prev,
                educationLevelDivisionId: option?.value,
              }));
            }}
            isClearable={false}
          />
        </div>

        <div className="form-input">
          <label className="required" htmlFor="assessmentName">
            Assessment Name
          </label>
          <input
            className="form-control"
            type="text"
            name="assessmentName"
            value={assessmentName}
            onChange={(e) => setAssessmentName(e.target.value)}
          />
        </div>

        <div className="form-input">
          <label className="required" htmlFor="assessmentTypeCode">
            Assessment Type
          </label>
          <Select
            value={
              assessmentType.find(
                (o) => o.value === formData.assessmentTypeCode,
              ) || null
            }
            options={assessmentType}
            onChange={(option) =>
              setFormData((prev) => ({
                ...prev,
                assessmentTypeCode: option?.value,
              }))
            }
            isClearable={false}
          />
        </div>

        <div className="form-input">
          <label className="required" htmlFor="class">
            Class
          </label>
          <Select
            value={yearLevelId || null}
            options={yearLevelOption}
            onChange={(selectedOptions) => {
              const newSelection = selectedOptions || [];

              const prevIds = (yearLevelId || []).map((item) => item.value);
              const newIds = newSelection.map((item) => item.value);

              const removedYearLevelIds = prevIds.filter(
                (id) => !newIds.includes(id),
              );

              setYearLevelId(newSelection);

              setAssessmentSubjectConfigurations((prev) =>
                prev?.filter(
                  (item) => !removedYearLevelIds.includes(item?.yearLevelId),
                ),
              );

              setAssessmentGeneralConfigurations((prev) =>
                prev?.filter(
                  (item) => !removedYearLevelIds.includes(item?.yearLevelId),
                ),
              );
            }}
            isDisabled={!formData.educationLevelDivisionId}
            isMulti
          />
        </div>

        <div className="form-input">
          <label htmlFor="aggregationConfiguration">
            Aggregation Configuration
          </label>
          <input
            type="button"
            name="aggregationConfiguration"
            value={"Select Assessment"}
            className="form-control"
            onClick={() => setAggregationConfigurationModal((prev) => !prev)}
          />
        </div>

        <div className="form-input">
          <label htmlFor="assessmentName">Start Time</label>
          <input
            className="form-control"
            type="time"
            name="startTime"
            value={formData?.startTime ?? ""}
            onChange={(e) => {
              const value = e.target.value;
              setFormData((prev) => ({
                ...prev,
                startTime: value,
              }));
            }}
          />
        </div>

        <div className="form-input">
          <label htmlFor="duration">Duration</label>
          <div style={{ gap: "8px" }} className="flex">
            <div style={{ gap: "8px", flex: 1 }} className="flex">
              <input
                className="form-control"
                type="number"
                min={0}
                max={24}
                value={formData?.hour ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (
                    Number(value) > 24 ||
                    Number(value) < 0 ||
                    value?.length > 2
                  ) {
                    swal("Oops", "Enter valid hour");
                    e.preventDefault();
                    return;
                  }

                  setFormData((prev) => ({
                    ...prev,
                    hour: value,
                  }));
                }}
                placeholder="Enter hour"
              />
              <span>Hour</span>
            </div>
            <div style={{ gap: "8px", flex: 1 }} className="flex">
              <input
                className="form-control"
                type="number"
                min={0}
                max={59}
                value={formData?.minutes}
                onChange={(e) => {
                  const value = e.target.value;
                  if (
                    Number(value) > 59 ||
                    Number(value) < 0 ||
                    value?.length > 2
                  ) {
                    swal("Oops", "Enter valid minutes");
                    e.preventDefault();
                    return;
                  }

                  setFormData((prev) => ({
                    ...prev,
                    minutes: value,
                  }));
                }}
                placeholder="Enter minutes"
              />
              <span>Minutes</span>
            </div>
          </div>
        </div>

        <div className="form-input recording-date">
          <input
            type="checkbox"
            checked={recordingDateCheckbox}
            name="recordingDate"
            onChange={() => setRecordingDateCheckbox((prev) => !prev)}
          />
          <p style={{ margin: 0 }}>Set Recording Deadline Dates</p>
          <Select
            value={recordingDateOption?.find(
              (o) => o.value === recordingDate?.date,
            )}
            options={recordingDateOption}
            onChange={(option) =>
              setRecordingDate((prev) => ({
                ...prev,
                date: option?.value,
              }))
            }
            styles={{
              control: { width: "150px" },
            }}
            isClearable={false}
            isDisabled={!recordingDateCheckbox}
          />

          <Select
            value={recordingOption?.find(
              (o) => o.value === recordingDate?.type,
            )}
            options={recordingOption}
            onChange={(option) =>
              setRecordingDate((prev) => ({
                ...prev,
                type: option?.value,
              }))
            }
            styles={{
              control: { width: "150px" },
            }}
            isClearable={false}
            isDisabled={!recordingDateCheckbox}
          />
          <p style={{ margin: 0 }}>After Recording Date</p>
        </div>
      </div>

      <CustomModal
        isOpen={aggregationConfigurationModal}
        toggle={() => setAggregationConfigurationModal((prev) => !prev)}
        modalHeader={"Select Assessment"}
        size="md"
        children={
          <AssessmentConfigurationModal
            aggregationWeightConfiguration={aggregationWeightConfiguration}
            setAggregationWeightConfiguration={
              setAggregationWeightConfiguration
            }
            closeModal={() => setAggregationConfigurationModal((prev) => !prev)}
            assessments={assessmentsOption}
          />
        }
      />
    </>
  );
};

export default AssessmentDetailForm;
